import React, { createContext, useContext, useReducer, useEffect } from 'react';

// Importar traducciones para mostrar grindType correctamente
import esMessages from '../intl/es.json';
import enMessages from '../intl/en.json';

// Helper para obtener la traducción del grindType
const getGrindTypeTranslation = (grindTypeKey, locale = 'es') => {
  const messages = locale === 'en' ? enMessages : esMessages;
  const translationKey = `producto.molienda.opciones.${grindTypeKey}`;
  
  // Navegar por el objeto de traducciones
  const keys = translationKey.split('.');
  let translation = messages;
  
  for (const key of keys) {
    translation = translation?.[key];
  }
  
  return translation || grindTypeKey; // Fallback a la clave si no se encuentra traducción
};

// Crear el contexto
const ShopContext = createContext();

// Estados iniciales
const initialState = {
  cart: [],
  totalItems: 0,
  subtotal: 0,
  shippingCost: 0,
  totalPrice: 0,
  customer: {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    address2: '',
    postalCode: '',
    province: '',
    city: '',
    country: '',
    acceptTerms: false,
    acceptMarketing: false,
    consentTimestamp: null // Para registrar cuándo se dieron los consentimientos
  },
  isCheckingOut: false
};

// Tipos de acciones
const ACTIONS = {
  ADD_TO_CART: 'ADD_TO_CART',
  REMOVE_FROM_CART: 'REMOVE_FROM_CART',
  UPDATE_QUANTITY: 'UPDATE_QUANTITY',
  CLEAR_CART: 'CLEAR_CART',
  SET_CUSTOMER_DATA: 'SET_CUSTOMER_DATA',
  LOAD_CART: 'LOAD_CART',
  SET_CHECKOUT_STATUS: 'SET_CHECKOUT_STATUS'
};

// Reducer para manejar el estado
function shopReducer(state, action) {
  switch (action.type) {
    case ACTIONS.ADD_TO_CART: {
      const { product } = action.payload;
      const existingItemIndex = state.cart.findIndex(
        item => 
          item.id === product.id && 
          item.size === product.size && 
          item.grindType === product.grindType
      );

      let newCart;
      if (existingItemIndex >= 0) {
        // Si el producto ya existe, actualizar cantidad
        newCart = state.cart.map((item, index) => 
          index === existingItemIndex 
            ? { ...item, quantity: item.quantity + product.quantity }
            : item
        );
      } else {
        // Si es un producto nuevo, añadirlo al carrito
        newCart = [...state.cart, { ...product, cartId: Date.now() }];
      }

      const totals = calculateTotals(newCart, state.customer.country);
      return {
        ...state,
        cart: newCart,
        ...totals
      };
    }

    case ACTIONS.REMOVE_FROM_CART: {
      const newCart = state.cart.filter(item => item.cartId !== action.payload.cartId);
      const totals = calculateTotals(newCart, state.customer.country);
      return {
        ...state,
        cart: newCart,
        ...totals
      };
    }

    case ACTIONS.UPDATE_QUANTITY: {
      const { cartId, quantity } = action.payload;
      
      if (quantity <= 0) {
        // Si la cantidad es 0 o menor, eliminar el producto
        return shopReducer(state, { 
          type: ACTIONS.REMOVE_FROM_CART, 
          payload: { cartId } 
        });
      }

      const newCart = state.cart.map(item =>
        item.cartId === cartId ? { ...item, quantity } : item
      );
      
      const totals = calculateTotals(newCart, state.customer.country);
      return {
        ...state,
        cart: newCart,
        ...totals
      };
    }

    case ACTIONS.CLEAR_CART: {
      return {
        ...state,
        cart: [],
        totalItems: 0,
        subtotal: 0,
        shippingCost: 0,
        totalPrice: 0
      };
    }

    case ACTIONS.SET_CUSTOMER_DATA: {
      const newCustomer = { ...state.customer, ...action.payload };
      
      // Si cambió el país, recalcular totales
      const countryChanged = state.customer.country !== newCustomer.country;
      if (countryChanged && state.cart.length > 0) {
        const totals = calculateTotals(state.cart, newCustomer.country);
        return {
          ...state,
          customer: newCustomer,
          ...totals
        };
      }
      
      return {
        ...state,
        customer: newCustomer
      };
    }

    case ACTIONS.SET_CHECKOUT_STATUS: {
      return {
        ...state,
        isCheckingOut: action.payload
      };
    }

    case ACTIONS.LOAD_CART: {
      const totals = calculateTotals(action.payload, state.customer.country);
      return {
        ...state,
        cart: action.payload,
        ...totals
      };
    }

    default:
      return state;
  }
}

// Función para calcular totales
function calculateTotals(cart, customerCountry = '') {
  const totalItems = cart.reduce((sum, item) => sum + item.quantity, 0);
  const subtotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  
  // Determinar si es España o internacional
  const isSpain = customerCountry === 'España' || customerCountry === 'Spain' || customerCountry === '';
  
  // Calcular gastos de envío según el país
  let shippingCost = 0;
  if (subtotal > 0) {
    if (isSpain) {
      // España: 4,99€ si < 50€, gratis si >= 50€
      shippingCost = subtotal < 50 ? 4.99 : 0;
    } else {
      // Internacional: 15€ si < 100€, gratis si >= 100€
      shippingCost = subtotal < 100 ? 15.00 : 0;
    }
  }
  
  const totalPrice = subtotal + shippingCost;
  
  return { 
    totalItems, 
    subtotal: Math.round(subtotal * 100) / 100, // Redondear a 2 decimales
    shippingCost: Math.round(shippingCost * 100) / 100,
    totalPrice: Math.round(totalPrice * 100) / 100
  };
}

// Hook personalizado para usar el contexto
export const useShop = () => {
  const context = useContext(ShopContext);
  if (!context) {
    throw new Error('useShop debe ser usado dentro de ShopProvider');
  }
  return context;
};

// Proveedor del contexto
export const ShopProvider = ({ children }) => {
  const [state, dispatch] = useReducer(shopReducer, initialState);

  // Cargar carrito desde localStorage al inicializar
  useEffect(() => {
    const savedCart = localStorage.getItem('torresflorez-cart');
    const savedCustomer = localStorage.getItem('torresflorez-customer');
    
    if (savedCart) {
      try {
        const cart = JSON.parse(savedCart);
        dispatch({ type: ACTIONS.LOAD_CART, payload: cart });
      } catch (error) {
        console.error('Error loading cart from localStorage:', error);
      }
    }

    if (savedCustomer) {
      try {
        const customer = JSON.parse(savedCustomer);
        dispatch({ type: ACTIONS.SET_CUSTOMER_DATA, payload: customer });
      } catch (error) {
        console.error('Error loading customer data from localStorage:', error);
      }
    }
  }, []);

  // Guardar carrito en localStorage cuando cambie
  useEffect(() => {
    if (state.cart.length > 0) {
      localStorage.setItem('torresflorez-cart', JSON.stringify(state.cart));
    } else {
      localStorage.removeItem('torresflorez-cart');
    }
  }, [state.cart]);

  // Guardar datos del cliente en localStorage cuando cambien
  useEffect(() => {
    if (state.customer.firstName || state.customer.email) {
      localStorage.setItem('torresflorez-customer', JSON.stringify(state.customer));
    }
  }, [state.customer]);

  // Funciones de acción
  const addToCart = (product) => {
    dispatch({ type: ACTIONS.ADD_TO_CART, payload: { product } });
  };

  const removeFromCart = (cartId) => {
    dispatch({ type: ACTIONS.REMOVE_FROM_CART, payload: { cartId } });
  };

  const updateQuantity = (cartId, quantity) => {
    dispatch({ type: ACTIONS.UPDATE_QUANTITY, payload: { cartId, quantity } });
  };

  const clearCart = () => {
    dispatch({ type: ACTIONS.CLEAR_CART });
    localStorage.removeItem('torresflorez-cart');
  };

  const setCustomerData = (customerData) => {
    dispatch({ type: ACTIONS.SET_CUSTOMER_DATA, payload: customerData });
  };

  const setCheckoutStatus = (status) => {
    dispatch({ type: ACTIONS.SET_CHECKOUT_STATUS, payload: status });
  };

  // Función para validar datos del cliente
  const validateCustomerData = () => {
    const { firstName, lastName, email, phone, address, postalCode, province, city, country } = state.customer;
    
    const errors = {};
    
    if (!firstName.trim()) errors.firstName = 'El nombre es obligatorio';
    if (!lastName.trim()) errors.lastName = 'Los apellidos son obligatorios';
    if (!email.trim()) {
      errors.email = 'El email es obligatorio';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.email = 'El email no es válido';
    }
    if (!phone.trim()) errors.phone = 'El teléfono es obligatorio';
    if (!address.trim()) errors.address = 'La dirección es obligatoria';
    if (!postalCode.trim()) errors.postalCode = 'El código postal es obligatorio';
    if (!province.trim()) errors.province = 'La provincia/estado es obligatoria';
    if (!city.trim()) errors.city = 'La población es obligatoria';
    if (!country.trim()) errors.country = 'El país es obligatorio';
    
    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  };

  // Función para crear hash seguro del carrito
  const createSecureCartHash = () => {
    const cartData = {
      items: state.cart.map(item => ({
        id: item.id,
        name: item.name,
        size: item.size,
        grindType: item.grindType,
        quantity: item.quantity,
        price: item.price
      })),
      totalPrice: state.totalPrice,
      totalItems: state.totalItems,
      timestamp: Date.now()
    };
    
    // Crear un hash simple basado en los datos del carrito
    const cartString = JSON.stringify(cartData);
    let hash = 0;
    for (let i = 0; i < cartString.length; i++) {
      const char = cartString.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    
    return {
      hash: Math.abs(hash).toString(16),
      data: cartData
    };
  };

  // Función para obtener el resumen del pedido
  const getOrderSummary = () => {
    return {
      items: state.cart,
      totalItems: state.totalItems,
      totalPrice: state.totalPrice,
      customer: state.customer,
      orderDate: new Date().toISOString()
    };
  };

  // Función para formatear el mensaje de WhatsApp
  const getWhatsAppMessage = () => {
    if (state.cart.length === 0) return '';

    let message = '🛒 *Nuevo Pedido - Torres Flórez Coffee*\n\n';
    
    // Datos del cliente
    if (state.customer.firstName || state.customer.lastName) {
      message += `👤 *Cliente:* ${state.customer.firstName} ${state.customer.lastName}\n`;
    }
    if (state.customer.phone) {
      message += `📞 *Teléfono:* ${state.customer.phone}\n`;
    }
    if (state.customer.email) {
      message += `📧 *Email:* ${state.customer.email}\n`;
    }
    if (state.customer.address) {
      message += `🏠 *Dirección:* ${state.customer.address}\n`;
      if (state.customer.address2) {
        message += `    ${state.customer.address2}\n`;
      }
      message += `    ${state.customer.postalCode} ${state.customer.city}\n`;
      message += `    ${state.customer.province}, ${state.customer.country}\n`;
    }
    
    message += '\n📦 *Productos:*\n';
    
    // Productos
    state.cart.forEach((item, index) => {
      message += `${index + 1}. ${item.name}\n`;
      
      // Solo mostrar tamaño y molido para productos de café
      if (item.size) {
        message += `   • Tamaño: ${item.size}\n`;
      }
      
      if (item.grindType) {
        const grindTypeTranslation = getGrindTypeTranslation(item.grindType);
        message += `   • Molido: ${grindTypeTranslation}\n`;
      }
      
      message += `   • Cantidad: ${item.quantity}\n`;
      message += `   • Precio: €${(item.price * item.quantity).toFixed(2)}\n\n`;
    });
    
    message += `💰 *Subtotal: €${state.subtotal.toFixed(2)}*\n`;
    
    if (state.shippingCost > 0) {
      message += `🚚 *Gastos de envío: €${state.shippingCost.toFixed(2)}*\n`;
    } else {
      message += `🚚 *Gastos de envío: GRATIS*\n`;
    }
    
    message += `💳 *Total: €${state.totalPrice.toFixed(2)}*\n\n`;
    message += '¡Gracias por tu pedido! 🙏';
    
    return encodeURIComponent(message);
  };

  const value = {
    // Estado
    cart: state.cart,
    totalItems: state.totalItems,
    subtotal: state.subtotal,
    shippingCost: state.shippingCost,
    totalPrice: state.totalPrice,
    customer: state.customer,
    isCheckingOut: state.isCheckingOut,
    
    // Acciones
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    setCustomerData,
    setCheckoutStatus,
    
    // Utilidades
    getOrderSummary,
    getWhatsAppMessage,
    validateCustomerData,
    createSecureCartHash
  };

  return (
    <ShopContext.Provider value={value}>
      {children}
    </ShopContext.Provider>
  );
};

export default ShopContext;
